import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { hubApi, PLAN_PRICING, STATUS_STYLE, BILLING_PERIOD_LABEL, calcFinalAmount } from "../api.js";
import { useIsMobile } from "../hooks/useIsMobile.js";

/* ─── helpers ─────────────────────────────────────────────── */
const fmt     = d => d ? new Date(d).toLocaleDateString("ro-RO") : "—";
const fmtLong = d => d ? new Date(d).toLocaleDateString("ro-RO", { day:"numeric", month:"long", year:"numeric" }) : "—";
const fmtCur  = (a, c) => (a != null && a !== "") ? `${Number(a).toLocaleString("ro-RO")} ${c||"EUR"}` : "—";
const toInput = d => d ? new Date(d).toISOString().split("T")[0] : "";
const daysUntil = d => d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null;

/* ─── feature flags constants ─────────────────────────────── */
const FF_PLANS = [
  { id:1, label:"Light",    color:"#3b82f6" },
  { id:2, label:"Standard", color:"#7c3aed" },
  { id:3, label:"Premium",  color:"#f97316" },
];
const FEATURE_GROUPS = [
  { group:"Core (Light+)", minPlan:1, color:"#3b82f6", features:[
    { key:"menuEnabled", label:"Menu" },
    { key:"qrCodeEnabled", label:"QR Codes" },
    { key:"ordersEnabled", label:"Orders" },
    { key:"categoriesEnabled", label:"Categories" },
  ]},
  { group:"Standard+", minPlan:2, color:"#7c3aed", features:[
    { key:"analyticsEnabled", label:"Analytics" },
    { key:"salesReportEnabled", label:"Sales Reports" },
    { key:"promoCodesEnabled", label:"Promo Codes" },
    { key:"ratingEnabled", label:"Ratings" },
    { key:"tipsEnabled", label:"Tips" },
    { key:"ordersHistoryEnabled", label:"Orders History" },
  ]},
  { group:"Premium+", minPlan:3, color:"#f97316", features:[
    { key:"reservationEnabled", label:"Reservations" },
    { key:"takeawayEnabled", label:"Takeaway" },
    { key:"deliveryEnabled", label:"Delivery" },
    { key:"waiterRequestsEnabled", label:"Waiter Requests" },
    { key:"autoPrintEnabled", label:"Auto Print" },
    { key:"multiLanguageEnabled", label:"Multi-Language" },
    { key:"dianaAiEnabled", label:"Diana AI" },
  ]},
];

const PLANS        = [1,2,3];
const CURRENCY_OPTS= ["EUR","RON","USD","GBP"];
const STATUS_OPTS  = [
  { v:"pending", l:"În așteptare" },
  { v:"paid",    l:"Plătit" },
  { v:"overdue", l:"Restanță" },
];

/* ─── widget definitions ──────────────────────────────────── */
const WIDGETS = [
  {
    id:"billing", title:"Facturare",
    subtitle:"Gestionează factura curentă",
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>,
    accent:"#4f46e5", bg:"#eef2ff",
  },
  {
    id:"accounts", title:"Creare cont",
    subtitle:"Admin, Waiter sau Orderly",
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="16" y1="11" x2="22" y2="11"/></svg>,
    accent:"#059669", bg:"#ecfdf5",
  },
  {
    id:"flags", title:"Feature Flags",
    subtitle:"Plan & funcționalități active",
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>,
    accent:"#d97706", bg:"#fffbeb",
  },
  {
    id:"info", title:"Informații",
    subtitle:"Date restaurant & contract",
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
    accent:"#0891b2", bg:"#ecfeff",
  },
  {
    id:"history", title:"Istoric plăți",
    subtitle:"Toate tranzacțiile înregistrate",
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
    accent:"#7c3aed", bg:"#f5f3ff",
  },
  {
    id:"notes", title:"Note interne",
    subtitle:"Observații echipă Orderly",
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
    accent:"#dc2626", bg:"#fef2f2",
  },
];

/* ══════════════════════════════════════════════════════════════ */
export default function RestaurantPage() {
  const { id } = useParams();
  const nav    = useNavigate();
  const isMobile = useIsMobile();
  const fileRef  = useRef();

  /* view: null = widget grid, or widget id = detail page */
  const [view, setView] = useState(null);

  /* restaurant */
  const [r, setR]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);

  /* billing */
  const [savingBill, setSavingBill] = useState(false);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [dragging, setDragging]       = useState(false);
  const [bill, setBill] = useState({ amount:"", currency:"EUR", dueDate:"", billingStatus:"pending" });

  /* info */
  const [savingInfo, setSavingInfo] = useState(false);
  const [info, setInfo] = useState({
    name:"", slug:"", companyName:"", vatNumber:"", address:"",
    contactPerson:"", contactEmail:"", contactPhone:"",
    backendUrl:"", frontendUrl:"", adminUrl:"",
    plan:1, billingPeriod:"monthly",
    discountType:"none", discountValue:0, discountNote:"", discountExpiry:"",
    contractStart:"", contractEnd:"",
  });

  /* notes */
  const [noteText, setNoteText]   = useState("");
  const [savingNote, setSavingNote] = useState(false);

  /* feature flags */
  const [customization, setCustomization] = useState(null);
  const [ffPlan, setFfPlan]     = useState(1);
  const [ffFeatures, setFfFeatures] = useState({});
  const [ffSaving, setFfSaving] = useState(false);
  const [ffLoading, setFfLoading] = useState(false);

  /* create account */
  const [newAcc, setNewAcc]   = useState({ name:"", email:"", password:"", userRole:"Admin", securityToken:"" });
  const [accSaving, setAccSaving] = useState(false);

  /* ─── load ── */
  const loadCustomization = () => {
    setFfLoading(true);
    hubApi.getCustomization(id)
      .then(({ data }) => {
        if (data.customization) {
          setCustomization(data.customization);
          setFfPlan(Math.min(data.customization.partnerPlan ?? 1, 3));
          setFfFeatures(data.customization.features ?? {});
        }
      })
      .catch(() => {})
      .finally(() => setFfLoading(false));
  };

  useEffect(() => {
    hubApi.getOne(id)
      .then(({ data }) => {
        if (!data.success) { toast.error("Restaurant negăsit."); return; }
        const x = data.restaurant;
        setR(x);
        setInfo({
          name:x.name||"", slug:x.slug||"", companyName:x.companyName||"",
          vatNumber:x.vatNumber||"", address:x.address||"",
          contactPerson:x.contactPerson||"", contactEmail:x.contactEmail||"",
          contactPhone:x.contactPhone||"",
          backendUrl:x.backendUrl||"", frontendUrl:x.frontendUrl||"", adminUrl:x.adminUrl||"",
          plan:x.plan||1, billingPeriod:x.billingPeriod||"monthly",
          discountType:x.discountType||"none", discountValue:x.discountValue||0,
          discountNote:x.discountNote||"", discountExpiry:toInput(x.discountExpiry),
          contractStart:toInput(x.contractStart), contractEnd:toInput(x.contractEnd),
        });
        setBill({
          amount:x.currentAmount||"", currency:x.currentCurrency||"EUR",
          dueDate:toInput(x.currentDueDate), billingStatus:x.billingStatus||"pending",
        });
      })
      .catch(() => toast.error("Eroare la încărcare."))
      .finally(() => setLoading(false));
    loadCustomization();
  }, [id]);

  /* ─── handlers ── */
  const setI = (k,v) => setInfo(p=>({...p,[k]:v}));
  const setB = (k,v) => setBill(p=>({...p,[k]:v}));

  const handleSaveInfo = async () => {
    setSavingInfo(true);
    try {
      const { data } = await hubApi.update(id, info);
      if (data.success) { setR(data.restaurant); toast.success("Informații salvate."); }
      else toast.error(data.message);
    } catch { toast.error("Eroare."); }
    finally { setSavingInfo(false); }
  };

  const doSaveBilling = async () => {
    setConfirmOverwrite(false);
    setSavingBill(true);
    try {
      let invoiceUrl = r?.currentInvoiceUrl || "";
      if (invoiceFile) {
        const { data: up } = await hubApi.uploadInvoice(id, invoiceFile);
        if (!up.success) { toast.error(up.message); setSavingBill(false); return; }
        invoiceUrl = up.invoiceUrl;
        setInvoiceFile(null);
      }
      const { data } = await hubApi.billing(id, { ...bill, invoiceUrl });
      if (data.success) {
        setR(p => ({ ...p, billingStatus:bill.billingStatus, currentAmount:bill.amount,
          currentDueDate:bill.dueDate, currentCurrency:bill.currency, currentInvoiceUrl:invoiceUrl }));
        toast.success("Factură salvată.");
      } else toast.error(data.message);
    } catch { toast.error("Eroare."); }
    finally { setSavingBill(false); }
  };

  const handleSaveBilling = () => {
    if (r?.billingStatus !== 'paid' && Number(r?.currentAmount) > 0) setConfirmOverwrite(true);
    else doSaveBilling();
  };

  const handleToggleActive = async () => {
    try {
      const { data } = await hubApi.toggle(id);
      if (data.success) setR(p=>({...p, isActive:data.isActive}));
    } catch { toast.error("Eroare."); }
  };

  const handleMarkPaid = async () => {
    try {
      const { data } = await hubApi.markPaid(id);
      if (data.success) {
        setR(p=>({...p,billingStatus:"paid"}));
        setBill(p=>({...p,billingStatus:"paid"}));
        toast.success("Marcat ca plătit.");
      }
    } catch { toast.error("Eroare."); }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      const { data } = await hubApi.addNote(id, noteText);
      if (data.success) { setR(p=>({...p, notes:data.notes})); setNoteText(""); }
    } catch { toast.error("Eroare."); }
    finally { setSavingNote(false); }
  };

  const handleDeleteNote = async (nid) => {
    try {
      const { data } = await hubApi.deleteNote(id, nid);
      if (data.success) setR(p=>({...p, notes:data.notes}));
    } catch { toast.error("Eroare."); }
  };

  const handleSaveFeatureFlags = async () => {
    if (!customization?._id) { toast.error("Nu există customizare pentru acest restaurant."); return; }
    setFfSaving(true);
    const PLAN_LABELS = ['Disabled','Light','Standard','Premium'];
    try {
      const { data } = await hubApi.updateCustomization(id, {
        partnerPlan: ffPlan,
        partnerPlanLabel: PLAN_LABELS[ffPlan] ?? 'Light',
        features: ffFeatures,
      });
      if (data.success) { setCustomization(data.customization); toast.success("Feature flags salvate."); }
      else toast.error(data.message);
    } catch { toast.error("Eroare."); }
    finally { setFfSaving(false); }
  };

  const handlePlanChange = (newPlan) => {
    setFfPlan(newPlan);
    const updated = { ...ffFeatures };
    FEATURE_GROUPS.forEach(g => g.features.forEach(f => {
      if (newPlan < g.minPlan) updated[f.key] = false;
      else if (!updated[f.key]) updated[f.key] = true;
    }));
    setFfFeatures(updated);
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setAccSaving(true);
    const backendUrl = r?.backendUrl || "https://api.orderly.ro";
    try {
      const res = await fetch(`${backendUrl}/admin/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newAcc),
      });
      const data = await res.json();
      if (data.success) {
        setNewAcc({ name:"", email:"", password:"", userRole:"Admin", securityToken: newAcc.securityToken });
        toast.success(`Cont "${newAcc.name}" creat (${newAcc.userRole}).`);
      } else toast.error(data.message || "Eroare.");
    } catch { toast.error("Eroare de rețea."); }
    finally { setAccSaving(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f?.type === "application/pdf") setInvoiceFile(f);
    else toast.error("Doar fișiere PDF.");
  };

  /* ─── loading / not found ── */
  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", background:"#f4f6f9" }}>
      <div style={{ textAlign:"center" }}>
        <svg style={{ animation:"spin 0.8s linear infinite" }} width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2.5" strokeLinecap="round"><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>
        <p style={{ margin:"12px 0 0", fontSize:14, color:"#9ca3af" }}>Se încarcă...</p>
      </div>
    </div>
  );
  if (!r) return <div style={{ padding:48, color:"#ef4444" }}>Restaurant negăsit.</div>;

  const planObj  = PLAN_PRICING[r.plan] || PLAN_PRICING[1];
  const ss       = STATUS_STYLE[r.billingStatus] || STATUS_STYLE.pending;
  const days     = daysUntil(r.currentDueDate);
  const finalAmt = calcFinalAmount(info.plan, info.billingPeriod, info.discountType, info.discountValue);

  /* widget preview meta */
  const widgetMeta = {
    billing:  { preview: r.currentAmount ? fmtCur(r.currentAmount, r.currentCurrency) : "Fără factură activă",
                sub: r.billingStatus === "paid" ? "✓ Achitat" : days != null ? `Scadent în ${Math.max(0,days)} zile` : "Nesolicitat" },
    accounts: { preview: "Creare utilizator", sub: "Admin · Waiter · Orderly" },
    flags:    { preview: customization ? `Plan ${FF_PLANS.find(p=>p.id===ffPlan)?.label||"Light"}` : "Fără customizare",
                sub: customization ? `${Object.values(ffFeatures).filter(Boolean).length} funcții active` : "Configurați din Admin" },
    info:     { preview: r.name, sub: `${r.slug}.orderly.ro` },
    history:  { preview: `${r.paymentHistory?.length||0} plăți`, sub: r.paymentHistory?.length ? `Ultima: ${fmt(r.paymentHistory.at(-1)?.dueDate)}` : "Fără plăți înregistrate" },
    notes:    { preview: `${r.notes?.length||0} note`, sub: r.notes?.length ? r.notes.at(-1).text.slice(0,50)+"…" : "Nicio notă adăugată" },
  };

  const activeWidget = WIDGETS.find(w => w.id === view);

  /* props passed to detail views */
  const viewProps = {
    r, bill, setB, setBill, savingBill, handleSaveBilling, invoiceFile, setInvoiceFile,
    dragging, setDragging, onDrop, fileRef,
    info, setI, savingInfo, handleSaveInfo,
    PLANS, CURRENCY_OPTS, STATUS_OPTS, PLAN_PRICING, BILLING_PERIOD_LABEL, finalAmt,
    noteText, setNoteText, savingNote, handleAddNote, handleDeleteNote,
    customization, ffLoading, ffPlan, ffFeatures, setFfFeatures, ffSaving,
    handleSaveFeatureFlags, handlePlanChange, FF_PLANS, FEATURE_GROUPS,
    newAcc, setNewAcc, accSaving, handleCreateAccount,
    fmt, fmtLong, fmtCur,
  };

  /* ═══════════════════════════════════════════════════════════ */
  return (
    <div style={{ minHeight:"100vh", background:"#f4f6f9", fontFamily:"'Plus Jakarta Sans',system-ui,sans-serif", color:"#111827" }}>
      <style>{`
        *,*::before,*::after{box-sizing:border-box;}
        @keyframes spin{to{transform:rotate(360deg);}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(14px);}to{opacity:1;transform:translateY(0);}}
        @keyframes fadeIn{from{opacity:0;}to{opacity:1;}}
        .wg-card{transition:box-shadow .22s,transform .22s,border-color .22s;cursor:pointer;}
        .wg-card:hover{transform:translateY(-3px);box-shadow:0 12px 32px rgba(0,0,0,0.1) !important;}
        .fade-up{animation:fadeUp 0.35s cubic-bezier(0.16,1,0.3,1) both;}
        .fade-in{animation:fadeIn 0.25s ease both;}
        input,select,textarea{outline:none;font-family:inherit;}
        input:focus,select:focus,textarea:focus{border-color:#6366f1 !important;box-shadow:0 0 0 3px rgba(99,102,241,0.12) !important;}
        input::placeholder,textarea::placeholder{color:#c4cdd8;}
        .hist-row:hover{background:#f9fafb !important;}
        .note-row:hover{background:#f9fafb !important;}
        ::-webkit-scrollbar{width:4px;} ::-webkit-scrollbar-thumb{background:#e2e8f0;border-radius:99px;}
      `}</style>

      {/* ── CONFIRM OVERWRITE MODAL ── */}
      {confirmOverwrite && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", backdropFilter:"blur(4px)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
          <div className="fade-up" style={{ background:"#fff", borderRadius:18, padding:"32px 28px", maxWidth:400, width:"100%", boxShadow:"0 24px 60px rgba(0,0,0,0.18)", border:"1.5px solid #e8edf3" }}>
            <div style={{ fontSize:36, textAlign:"center", marginBottom:12 }}>⚠️</div>
            <h3 style={{ textAlign:"center", margin:"0 0 10px", fontSize:17, fontWeight:800 }}>Factură activă neachitată</h3>
            <p style={{ textAlign:"center", fontSize:14, color:"#6b7280", margin:"0 0 24px", lineHeight:1.6 }}>
              Există o factură de <strong style={{ color:"#111827" }}>{r.currentAmount} {r.currentCurrency}</strong> neachitată. Înlocuiești cu cea nouă?
            </p>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={()=>setConfirmOverwrite(false)} style={{ flex:1, padding:"11px", borderRadius:10, border:"1.5px solid #e5e7eb", background:"#fff", color:"#374151", fontSize:13, fontWeight:600, cursor:"pointer" }}>Anulează</button>
              <button onClick={doSaveBilling} style={{ flex:1, padding:"11px", borderRadius:10, border:"none", background:"#ef4444", color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer" }}>Da, înlocuiește</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ WIDGET DETAIL PAGE ══ */}
      {view && activeWidget && (
        <div className="fade-in" style={{ minHeight:"100vh" }}>
          {/* Detail header */}
          <div style={{ background:"#fff", borderBottom:"1.5px solid #e8edf3", padding: isMobile ? "16px" : "20px 32px", position:"sticky", top:0, zIndex:50 }}>
            <div style={{ maxWidth:900, margin:"0 auto", display:"flex", alignItems:"center", gap:14 }}>
              <button onClick={()=>setView(null)}
                style={{ display:"flex", alignItems:"center", gap:7, padding:"9px 16px", borderRadius:10, border:"1.5px solid #e5e7eb", background:"#fff", color:"#374151", fontSize:13, fontWeight:600, cursor:"pointer", flexShrink:0, transition:"all .15s" }}
                onMouseEnter={e=>{e.currentTarget.style.background="#f3f4f6";}}
                onMouseLeave={e=>{e.currentTarget.style.background="#fff";}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                Înapoi
              </button>
              <div style={{ width:1, height:24, background:"#e8edf3" }}/>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ width:36, height:36, borderRadius:10, background:activeWidget.bg, display:"flex", alignItems:"center", justifyContent:"center", color:activeWidget.accent, flexShrink:0 }}>
                  {activeWidget.icon}
                </div>
                <div>
                  <p style={{ margin:0, fontSize:15, fontWeight:800, color:"#111827" }}>{activeWidget.title}</p>
                  <p style={{ margin:0, fontSize:12, color:"#9ca3af" }}>{r.name}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Detail content */}
          <div style={{ maxWidth:900, margin:"0 auto", padding: isMobile ? "20px 14px" : "32px" }}>
            <WidgetDetail id={view} accent={activeWidget.accent} {...viewProps} />
          </div>
        </div>
      )}

      {/* ══ WIDGET GRID (main view) ══ */}
      {!view && (
        <>
          {/* Page header */}
          <div style={{ background:"#fff", borderBottom:"1.5px solid #e8edf3", padding: isMobile ? "16px" : "20px 32px" }}>
            <div style={{ maxWidth:900, margin:"0 auto" }}>
              <button onClick={()=>nav("/")}
                style={{ display:"flex", alignItems:"center", gap:6, background:"none", border:"none", cursor:"pointer", color:"#9ca3af", fontSize:13, fontWeight:500, padding:0, marginBottom:16 }}
                onMouseEnter={e=>e.currentTarget.style.color="#374151"}
                onMouseLeave={e=>e.currentTarget.style.color="#9ca3af"}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                Toate restaurantele
              </button>

              <div style={{ display:"flex", alignItems: isMobile ? "flex-start" : "center", gap:16, flexDirection: isMobile ? "column" : "row" }}>
                {/* Avatar */}
                <div style={{ width:52, height:52, borderRadius:14, background:planObj.bg, border:`2px solid ${planObj.border}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, fontWeight:900, color:planObj.color, flexShrink:0 }}>
                  {r.name.charAt(0).toUpperCase()}
                </div>

                <div style={{ flex:1 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginBottom:6 }}>
                    <h1 style={{ margin:0, fontSize: isMobile ? 20 : 24, fontWeight:900, letterSpacing:"-0.5px" }}>{r.name}</h1>
                    <span style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:99, background:planObj.bg, color:planObj.color, border:`1px solid ${planObj.border}` }}>{planObj.name}</span>
                    <span style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:99, background:ss.bg, color:ss.color, border:`1px solid ${ss.border}` }}>{ss.label}</span>
                    {!r.isActive && <span style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:99, background:"#fef2f2", color:"#ef4444", border:"1px solid #fecaca" }}>INACTIV</span>}
                  </div>
                  <p style={{ margin:0, fontSize:13, color:"#9ca3af" }}>{r.slug}.orderly.ro &nbsp;·&nbsp; {r.contactEmail||"fără email"}</p>
                </div>

                {/* Quick actions */}
                <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                  {r.frontendUrl && (
                    <a href={r.frontendUrl} target="_blank" rel="noreferrer"
                      style={{ display:"flex", alignItems:"center", gap:5, padding:"8px 14px", borderRadius:9, border:"1.5px solid #e5e7eb", background:"#fff", color:"#374151", fontSize:12, fontWeight:600, textDecoration:"none", transition:"all .15s" }}
                      onMouseEnter={e=>{e.currentTarget.style.borderColor="#4f46e5";e.currentTarget.style.color="#4f46e5";}}
                      onMouseLeave={e=>{e.currentTarget.style.borderColor="#e5e7eb";e.currentTarget.style.color="#374151";}}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
                      Frontend
                    </a>
                  )}
                  {r.adminUrl && (
                    <a href={r.adminUrl} target="_blank" rel="noreferrer"
                      style={{ display:"flex", alignItems:"center", gap:5, padding:"8px 14px", borderRadius:9, border:"1.5px solid #e5e7eb", background:"#fff", color:"#374151", fontSize:12, fontWeight:600, textDecoration:"none", transition:"all .15s" }}
                      onMouseEnter={e=>{e.currentTarget.style.borderColor="#4f46e5";e.currentTarget.style.color="#4f46e5";}}
                      onMouseLeave={e=>{e.currentTarget.style.borderColor="#e5e7eb";e.currentTarget.style.color="#374151";}}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                      Admin
                    </a>
                  )}
                  {r.billingStatus !== "paid" && Number(r.currentAmount) > 0 && (
                    <button onClick={handleMarkPaid}
                      style={{ display:"flex", alignItems:"center", gap:5, padding:"8px 14px", borderRadius:9, border:"none", background:"#059669", color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer", boxShadow:"0 2px 8px rgba(5,150,105,0.3)" }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                      Marchează plătit
                    </button>
                  )}
                  <button onClick={handleToggleActive}
                    style={{ display:"flex", alignItems:"center", gap:7, padding:"8px 14px", borderRadius:9, border:`1.5px solid ${r.isActive?"#bbf7d0":"#fecaca"}`, background:r.isActive?"#f0fdf4":"#fef2f2", color:r.isActive?"#059669":"#dc2626", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                    <div style={{ width:7, height:7, borderRadius:"50%", background:r.isActive?"#10b981":"#ef4444" }}/>
                    {r.isActive ? "Activ" : "Inactiv"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* KPI strip */}
          <div style={{ maxWidth:900, margin:"0 auto", padding: isMobile ? "16px 14px 0" : "20px 32px 0" }}>
            <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)", gap:12, marginBottom:24 }}>
              {[
                { label:"Sumă curentă",   val: r.currentAmount ? fmtCur(r.currentAmount, r.currentCurrency) : "—", color:planObj.color },
                { label:"Scadent",        val: r.currentDueDate ? fmt(r.currentDueDate) : "—",                      color: days != null && days <= 7 && r.billingStatus !== "paid" ? "#dc2626" : "#d97706" },
                { label:"Plan activ",     val: planObj.name,                                                          color: planObj.color },
                { label:"Funcții active", val: customization ? `${Object.values(ffFeatures).filter(Boolean).length}/${Object.keys(ffFeatures).length}` : "—", color:"#7c3aed" },
              ].map((s,i) => (
                <div key={i} style={{ background:"#fff", borderRadius:12, border:"1.5px solid #e8edf3", padding:"14px 16px", boxShadow:"0 1px 3px rgba(0,0,0,0.05)" }}>
                  <p style={{ margin:"0 0 4px", fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", letterSpacing:"0.06em" }}>{s.label}</p>
                  <p style={{ margin:0, fontSize:16, fontWeight:800, color:s.color, letterSpacing:"-0.3px" }}>{s.val}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Widget grid */}
          <div style={{ maxWidth:900, margin:"0 auto", padding: isMobile ? "0 14px 32px" : "0 32px 40px" }}>
            <p style={{ fontSize:12, fontWeight:700, color:"#b0bac9", textTransform:"uppercase", letterSpacing:"0.8px", margin:"0 0 14px" }}>Acțiuni disponibile</p>
            <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap:14 }}>
              {WIDGETS.map((w, idx) => {
                const meta = widgetMeta[w.id] || {};
                return (
                  <div key={w.id} className="wg-card fade-up"
                    style={{ animationDelay:`${idx*0.06}s`, background:"#fff", borderRadius:16, border:"1.5px solid #e8edf3", padding:"22px", boxShadow:"0 1px 4px rgba(0,0,0,0.05)" }}
                    onClick={()=>setView(w.id)}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
                      <div style={{ width:48, height:48, borderRadius:13, background:w.bg, display:"flex", alignItems:"center", justifyContent:"center", color:w.accent }}>
                        {w.icon}
                      </div>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                    </div>
                    <p style={{ margin:"0 0 4px", fontSize:15, fontWeight:800, color:"#111827" }}>{w.title}</p>
                    <p style={{ margin:"0 0 16px", fontSize:12, color:"#9ca3af" }}>{w.subtitle}</p>
                    <div style={{ paddingTop:14, borderTop:"1.5px solid #f3f4f6" }}>
                      <p style={{ margin:0, fontSize:13, fontWeight:700, color:w.accent }}>{meta.preview}</p>
                      <p style={{ margin:"3px 0 0", fontSize:11, color:"#b0bac9" }}>{meta.sub}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   WIDGET DETAIL VIEWS
══════════════════════════════════════════════════════════════ */
function WidgetDetail({ id, accent, r, bill, setB, setBill, savingBill, handleSaveBilling,
  invoiceFile, setInvoiceFile, dragging, setDragging, onDrop, fileRef,
  info, setI, savingInfo, handleSaveInfo, PLANS, CURRENCY_OPTS, STATUS_OPTS, PLAN_PRICING,
  BILLING_PERIOD_LABEL, finalAmt, noteText, setNoteText, savingNote, handleAddNote, handleDeleteNote,
  customization, ffLoading, ffPlan, ffFeatures, setFfFeatures, ffSaving,
  handleSaveFeatureFlags, handlePlanChange, FF_PLANS, FEATURE_GROUPS,
  newAcc, setNewAcc, accSaving, handleCreateAccount,
  fmt, fmtLong, fmtCur }) {

  const INP = {
    width:"100%", padding:"10px 14px", borderRadius:9,
    border:"1.5px solid #e5e7eb", background:"#fff",
    color:"#111827", fontSize:13, outline:"none",
    transition:"border-color .2s, box-shadow .2s",
  };
  const CARD = { background:"#fff", borderRadius:14, border:"1.5px solid #e8edf3", padding:"24px", boxShadow:"0 1px 4px rgba(0,0,0,0.05)" };

  /* ── BILLING ── */
  if (id === "billing") return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
      <div style={CARD}>
        <SLabel accent={accent}>Sumă & status</SLabel>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 100px", gap:10, marginBottom:12 }}>
          <div>
            <FLabel>Sumă</FLabel>
            <input type="number" value={bill.amount} onChange={e=>setB("amount",e.target.value)} placeholder={`ex: ${finalAmt}`} style={INP}/>
          </div>
          <div>
            <FLabel>Valută</FLabel>
            <select value={bill.currency} onChange={e=>setB("currency",e.target.value)} style={INP}>
              {CURRENCY_OPTS.map(c=><option key={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div style={{ marginBottom:12 }}>
          <FLabel>Dată scadentă</FLabel>
          <input type="date" value={bill.dueDate} onChange={e=>setB("dueDate",e.target.value)} style={INP}/>
        </div>
        <div style={{ marginBottom:20 }}>
          <FLabel>Status</FLabel>
          <div style={{ display:"flex", gap:8, marginTop:6 }}>
            {STATUS_OPTS.map(o => {
              const cols = { pending:{c:"#d97706",bg:"#fffbeb",b:"#fde68a"}, paid:{c:"#059669",bg:"#f0fdf4",b:"#bbf7d0"}, overdue:{c:"#dc2626",bg:"#fef2f2",b:"#fecaca"} };
              const col = cols[o.v]; const active = bill.billingStatus===o.v;
              return (
                <button key={o.v} onClick={()=>setB("billingStatus",o.v)}
                  style={{ flex:1, padding:"9px", borderRadius:9, border:`1.5px solid ${active?col.b:"#e5e7eb"}`, background:active?col.bg:"#fff", color:active?col.c:"#9ca3af", fontWeight:700, fontSize:12, cursor:"pointer", transition:"all .15s" }}>
                  {o.l}
                </button>
              );
            })}
          </div>
        </div>
        <Div/>
        <SLabel accent={accent}>Sume rapide</SLabel>
        <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:20 }}>
          {PLANS.map(pid=>["monthly","quarterly","yearly"].map(per=>{
            const p=PLAN_PRICING[pid]; const a=p[per]||p.monthly;
            return (
              <button key={`${pid}-${per}`} onClick={()=>setBill(b=>({...b,amount:a,currency:"EUR"}))}
                style={{ padding:"5px 10px", borderRadius:7, border:"1.5px solid #e5e7eb", background:"#f9fafb", fontSize:11, fontWeight:600, cursor:"pointer", color:"#374151", transition:"all .15s" }}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=p.color;e.currentTarget.style.color=p.color;}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor="#e5e7eb";e.currentTarget.style.color="#374151";}}>
                <span style={{ color:p.color, fontWeight:800 }}>{p.name}</span> €{a}/{per==="monthly"?"lun":per==="quarterly"?"trim":"an"}
              </button>
            );
          }))}
        </div>
        <PBtn accent={accent} onClick={handleSaveBilling} loading={savingBill}>
          {invoiceFile ? "Salvează + încarcă PDF" : "Salvează factura"}
        </PBtn>
      </div>

      <div style={CARD}>
        <SLabel accent={accent}>Factură PDF</SLabel>
        {r.currentInvoiceUrl && !invoiceFile && (
          <div style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 14px", borderRadius:9, background:"#f0fdf4", border:"1.5px solid #bbf7d0", marginBottom:14 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            <span style={{ flex:1, fontSize:13, color:"#059669", fontWeight:500 }}>Factură activă</span>
            <a href={r.currentInvoiceUrl} target="_blank" rel="noreferrer" style={{ fontSize:12, color:"#059669", fontWeight:700, textDecoration:"none" }}>Descarcă →</a>
          </div>
        )}
        <div onClick={()=>fileRef.current.click()}
          onDragOver={e=>{e.preventDefault();setDragging(true);}}
          onDragLeave={()=>setDragging(false)}
          onDrop={onDrop}
          style={{ border:`2px dashed ${dragging||invoiceFile?accent:"#d1d5db"}`, borderRadius:12, padding:"40px 20px", textAlign:"center", cursor:"pointer", background:dragging||invoiceFile?`${accent}08`:"#fafafa", transition:"all .2s" }}>
          <input ref={fileRef} type="file" accept="application/pdf" style={{ display:"none" }} onChange={e=>setInvoiceFile(e.target.files[0]||null)}/>
          {invoiceFile ? (
            <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              <span style={{ fontSize:13, color:accent, fontWeight:600 }}>{invoiceFile.name}</span>
              <button onClick={e=>{e.stopPropagation();setInvoiceFile(null);}} style={{ background:"none", border:"none", cursor:"pointer", color:"#9ca3af", fontSize:18, lineHeight:1, padding:0 }}>×</button>
            </div>
          ) : (
            <div>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" style={{ marginBottom:10 }}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              <p style={{ margin:0, fontSize:13, color:"#9ca3af" }}>Trage PDF sau <span style={{ color:accent, fontWeight:600 }}>alege fișier</span></p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  /* ── ACCOUNTS ── */
  if (id === "accounts") return (
    <div style={{ maxWidth:520 }}>
      <div style={CARD}>
        <SLabel accent={accent}>Creare utilizator pentru panoul Admin</SLabel>
        <p style={{ fontSize:13, color:"#6b7280", margin:"0 0 20px", lineHeight:1.6 }}>
          Utilizatorul va putea accesa panoul Admin al restaurantului <strong style={{ color:"#374151" }}>({r?.backendUrl || "api.orderly.ro"})</strong>.
        </p>
        <form onSubmit={handleCreateAccount} style={{ display:"flex", flexDirection:"column", gap:14 }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <div><FLabel>Nume</FLabel><input value={newAcc.name} onChange={e=>setNewAcc(p=>({...p,name:e.target.value}))} placeholder="Ion Popescu" style={INP} required/></div>
            <div><FLabel>Email</FLabel><input type="email" value={newAcc.email} onChange={e=>setNewAcc(p=>({...p,email:e.target.value}))} placeholder="ion@restaurant.ro" style={INP} required/></div>
          </div>
          <div>
            <FLabel>Parolă</FLabel>
            <input type="password" value={newAcc.password} onChange={e=>setNewAcc(p=>({...p,password:e.target.value}))} placeholder="Minim 6 caractere" style={INP} required minLength={6}/>
          </div>
          <div>
            <FLabel>Rol</FLabel>
            <div style={{ display:"flex", gap:8, marginTop:6 }}>
              {[["Admin","#4f46e5","#eef2ff"],["Waiter","#059669","#ecfdf5"],["Orderly","#d97706","#fffbeb"]].map(([role,color,bg])=>(
                <button key={role} type="button" onClick={()=>setNewAcc(p=>({...p,userRole:role}))}
                  style={{ flex:1, padding:"10px", borderRadius:9, border:`1.5px solid ${newAcc.userRole===role?"currentColor":"#e5e7eb"}`, borderColor:newAcc.userRole===role?color:"#e5e7eb", background:newAcc.userRole===role?bg:"#fff", color:newAcc.userRole===role?color:"#9ca3af", fontWeight:700, fontSize:13, cursor:"pointer", transition:"all .15s" }}>
                  {role}
                </button>
              ))}
            </div>
          </div>
          <div>
            <FLabel>Security Token</FLabel>
            <input value={newAcc.securityToken} onChange={e=>setNewAcc(p=>({...p,securityToken:e.target.value}))} placeholder="Token de securitate" style={INP} required/>
            {customization?.securityToken && !newAcc.securityToken && (
              <button type="button" onClick={()=>setNewAcc(p=>({...p,securityToken:customization.securityToken}))}
                style={{ marginTop:6, fontSize:12, color:accent, background:"none", border:"none", cursor:"pointer", padding:0, fontWeight:600 }}>
                ↳ Auto-completează din customizare
              </button>
            )}
          </div>
          <PBtn accent={accent} loading={accSaving}>{`+ Creare cont ${newAcc.userRole}`}</PBtn>
        </form>
      </div>
    </div>
  );

  /* ── FEATURE FLAGS ── */
  if (id === "flags") return ffLoading ? (
    <p style={{ color:"#9ca3af", fontSize:14, padding:"32px 0" }}>Se încarcă feature flags...</p>
  ) : !customization ? (
    <div style={{ padding:"20px", background:"#fef2f2", border:"1.5px solid #fecaca", borderRadius:12, color:"#dc2626", fontSize:13 }}>
      ⚠️ Nu există document de customizare pentru acest restaurant. Configurați mai întâi din panoul Admin → Settings → Customization.
    </div>
  ) : (
    <div style={{ display:"grid", gridTemplateColumns:"240px 1fr", gap:20 }}>
      <div style={CARD}>
        <SLabel accent={accent}>Plan partener</SLabel>
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {FF_PLANS.map(p=>(
            <button key={p.id} onClick={()=>handlePlanChange(p.id)}
              style={{ padding:"12px 16px", borderRadius:10, border:`1.5px solid ${ffPlan===p.id?p.color:"#e5e7eb"}`, background:ffPlan===p.id?`${p.color}10`:"#fff", cursor:"pointer", textAlign:"left", transition:"all .15s", display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ width:10, height:10, borderRadius:"50%", background:ffPlan===p.id?p.color:"#e5e7eb", flexShrink:0 }}/>
              <span style={{ fontSize:14, fontWeight:700, color:ffPlan===p.id?p.color:"#374151" }}>{p.label}</span>
            </button>
          ))}
        </div>
        <Div/>
        <PBtn accent={accent} onClick={handleSaveFeatureFlags} loading={ffSaving}>Salvează</PBtn>
      </div>
      <div style={CARD}>
        <SLabel accent={accent}>Funcționalități</SLabel>
        <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
          {FEATURE_GROUPS.map(g=>(
            <div key={g.group}>
              <p style={{ fontSize:11, fontWeight:800, textTransform:"uppercase", letterSpacing:"0.08em", color:g.color, margin:"0 0 10px" }}>{g.group}</p>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
                {g.features.map(f=>{
                  const locked = ffPlan < g.minPlan;
                  const on = ffFeatures[f.key];
                  return (
                    <div key={f.key} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", borderRadius:9, border:"1.5px solid #f3f4f6", background:"#fafafa", opacity:locked?0.4:1 }}>
                      <span style={{ fontSize:12, fontWeight:600, color:"#374151" }}>{f.label}</span>
                      <button type="button" onClick={()=>!locked&&setFfFeatures(p=>({...p,[f.key]:!p[f.key]}))}
                        style={{ width:36, height:20, borderRadius:99, border:"none", cursor:locked?"not-allowed":"pointer", padding:0, background:on?g.color:"#e5e7eb", transition:"background .2s", display:"flex", alignItems:"center", justifyContent:on?"flex-end":"flex-start", flexShrink:0 }}>
                        <div style={{ width:16, height:16, borderRadius:"50%", background:"#fff", margin:"0 2px", boxShadow:"0 1px 3px rgba(0,0,0,0.2)" }}/>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  /* ── INFO ── */
  if (id === "info") return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
      <div style={CARD}>
        <SLabel accent={accent}>Plan & facturare</SLabel>
        <FLabel>Plan</FLabel>
        <div style={{ display:"flex", gap:8, marginBottom:14, marginTop:6 }}>
          {PLANS.map(pid=>{
            const p=PLAN_PRICING[pid];
            return (
              <button key={pid} onClick={()=>setI("plan",pid)}
                style={{ flex:1, padding:"10px 4px", borderRadius:9, border:`1.5px solid ${info.plan===pid?p.color:p.border}`, background:info.plan===pid?p.bg:"#fff", cursor:"pointer", transition:"all .15s" }}>
                <div style={{ fontSize:12, fontWeight:800, color:info.plan===pid?p.color:"#9ca3af" }}>{p.name}</div>
                <div style={{ fontSize:10, color:"#9ca3af", marginTop:2 }}>€{p.monthly}/lun</div>
              </button>
            );
          })}
        </div>
        <FLabel>Perioadă facturare</FLabel>
        <div style={{ display:"flex", gap:8, marginBottom:16, marginTop:6 }}>
          {[["monthly","Lunar"],["quarterly","Trimestrial"],["yearly","Anual"]].map(([v,l])=>(
            <button key={v} onClick={()=>setI("billingPeriod",v)}
              style={{ flex:1, padding:"9px", borderRadius:9, border:`1.5px solid ${info.billingPeriod===v?accent:"#e5e7eb"}`, background:info.billingPeriod===v?"#eef2ff":"#fff", color:info.billingPeriod===v?accent:"#9ca3af", fontWeight:600, fontSize:11, cursor:"pointer" }}>
              {l}
            </button>
          ))}
        </div>
        <Div/>
        <SLabel accent={accent}>Contact & companie</SLabel>
        {[["contactPerson","Persoană contact"],["contactEmail","Email"],["contactPhone","Telefon"],["companyName","Companie"],["vatNumber","CUI / VAT"],["address","Adresă"]].map(([k,ph])=>(
          <div key={k} style={{ marginBottom:10 }}>
            <FLabel>{ph}</FLabel>
            <input value={info[k]} onChange={e=>setI(k,e.target.value)} placeholder={ph} style={INP}/>
          </div>
        ))}
      </div>
      <div style={CARD}>
        <SLabel accent={accent}>Contract</SLabel>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:14 }}>
          <div><FLabel>Start contract</FLabel><input type="date" value={info.contractStart} onChange={e=>setI("contractStart",e.target.value)} style={INP}/></div>
          <div><FLabel>Expirare</FLabel><input type="date" value={info.contractEnd} onChange={e=>setI("contractEnd",e.target.value)} style={INP}/></div>
        </div>
        <Div/>
        <SLabel accent={accent}>URL-uri</SLabel>
        {[["frontendUrl","Frontend URL"],["adminUrl","Admin URL"],["backendUrl","API URL"]].map(([k,ph])=>(
          <div key={k} style={{ marginBottom:10 }}>
            <FLabel>{ph}</FLabel>
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              <input value={info[k]} onChange={e=>setI(k,e.target.value)} placeholder={ph} style={{ ...INP, flex:1 }}/>
              {info[k] && <a href={info[k]} target="_blank" rel="noreferrer" style={{ color:accent, textDecoration:"none", fontSize:18, flexShrink:0 }}>↗</a>}
            </div>
          </div>
        ))}
        <Div/>
        <PBtn accent={accent} onClick={handleSaveInfo} loading={savingInfo}>Salvează informații</PBtn>
      </div>
    </div>
  );

  /* ── HISTORY ── */
  if (id === "history") return !r.paymentHistory?.length ? (
    <div style={{ ...CARD, textAlign:"center", padding:"60px 24px" }}>
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" style={{ marginBottom:14 }}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
      <p style={{ margin:0, fontSize:15, fontWeight:600, color:"#374151" }}>Niciun istoric de plăți</p>
      <p style={{ margin:"6px 0 0", fontSize:13, color:"#9ca3af" }}>Plățile vor apărea aici după prima facturare.</p>
    </div>
  ) : (
    <div style={CARD}>
      <SLabel accent={accent}>{r.paymentHistory.length} tranzacții înregistrate</SLabel>
      <div style={{ borderRadius:10, border:"1.5px solid #e8edf3", overflow:"hidden" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
          <thead>
            <tr style={{ background:"#f9fafb" }}>
              {["Sumă","Scadent","Plătit la","Status","PDF"].map(h=>(
                <th key={h} style={{ padding:"11px 16px", textAlign:"left", fontSize:10, textTransform:"uppercase", letterSpacing:"0.6px", color:"#9ca3af", fontWeight:700, borderBottom:"1.5px solid #e8edf3" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {r.paymentHistory.slice().reverse().map((h,i)=>{
              const cols={ paid:{c:"#059669",bg:"#f0fdf4"}, pending:{c:"#d97706",bg:"#fffbeb"}, overdue:{c:"#dc2626",bg:"#fef2f2"} };
              const col=cols[h.status]||cols.pending;
              return (
                <tr key={i} className="hist-row" style={{ borderBottom:"1.5px solid #f3f4f6", transition:"background .12s" }}>
                  <td style={{ padding:"13px 16px", fontWeight:800, color:"#111827" }}>{h.amount ? `${h.amount} ${h.currency||"EUR"}` : "—"}</td>
                  <td style={{ padding:"13px 16px", color:"#6b7280" }}>{fmt(h.dueDate)}</td>
                  <td style={{ padding:"13px 16px", color:"#6b7280" }}>{h.paidAt?fmt(h.paidAt):"—"}</td>
                  <td style={{ padding:"13px 16px" }}>
                    <span style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:99, background:col.bg, color:col.c }}>
                      {h.status==="paid"?"Plătit":h.status==="overdue"?"Restanță":"În așteptare"}
                    </span>
                  </td>
                  <td style={{ padding:"13px 16px" }}>
                    {h.invoiceUrl
                      ? <a href={h.invoiceUrl} target="_blank" rel="noreferrer" style={{ color:accent, fontWeight:700, textDecoration:"none", fontSize:12 }}>↓ PDF</a>
                      : <span style={{ color:"#d1d5db" }}>—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  /* ── NOTES ── */
  if (id === "notes") return (
    <div style={{ maxWidth:640 }}>
      <div style={{ ...CARD, marginBottom:16 }}>
        <SLabel accent={accent}>Adaugă notă</SLabel>
        <div style={{ display:"flex", gap:10 }}>
          <input value={noteText} onChange={e=>setNoteText(e.target.value)} placeholder="Scrie o notă internă..."
            style={{ ...INP, flex:1 }}
            onKeyDown={e=>{ if(e.key==="Enter"&&!e.shiftKey){ e.preventDefault(); handleAddNote(); }}}/>
          <button onClick={handleAddNote} disabled={!noteText.trim()||savingNote}
            style={{ padding:"10px 20px", borderRadius:9, border:"none", background:noteText.trim()?accent:"#e5e7eb", color:noteText.trim()?"#fff":"#9ca3af", fontWeight:700, fontSize:13, cursor:noteText.trim()?"pointer":"not-allowed", flexShrink:0, transition:"all .15s" }}>
            {savingNote ? "..." : "+ Adaugă"}
          </button>
        </div>
      </div>
      {!r.notes?.length ? (
        <div style={{ ...CARD, textAlign:"center", padding:"48px 24px" }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" style={{ marginBottom:12 }}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          <p style={{ margin:0, fontSize:14, fontWeight:600, color:"#374151" }}>Nicio notă adăugată</p>
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {r.notes.slice().reverse().map((n,i)=>(
            <div key={n._id||i} className="note-row" style={{ background:"#fff", borderRadius:12, border:"1.5px solid #e8edf3", padding:"16px 18px", display:"flex", gap:12, transition:"background .12s" }}>
              <div style={{ width:30, height:30, borderRadius:"50%", background:"#eef2ff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:800, color:"#4f46e5", flexShrink:0 }}>A</div>
              <div style={{ flex:1, minWidth:0 }}>
                <p style={{ margin:"0 0 5px", fontSize:11, color:"#9ca3af" }}>{fmtLong(n.createdAt)}</p>
                <p style={{ margin:0, fontSize:13, color:"#374151", lineHeight:1.6, whiteSpace:"pre-wrap" }}>{n.text}</p>
              </div>
              <button onClick={()=>handleDeleteNote(n._id)}
                style={{ background:"none", border:"none", cursor:"pointer", color:"#d1d5db", fontSize:20, lineHeight:1, flexShrink:0, padding:0, transition:"color .15s" }}
                onMouseEnter={e=>e.currentTarget.style.color="#ef4444"}
                onMouseLeave={e=>e.currentTarget.style.color="#d1d5db"}>×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return null;
}

/* ── Shared micro-components ─────────────────────────────── */
function SLabel({ children, accent }) {
  return <p style={{ fontSize:10, fontWeight:800, textTransform:"uppercase", letterSpacing:"0.1em", color:accent, margin:"0 0 14px" }}>{children}</p>;
}
function FLabel({ children }) {
  return <label style={{ display:"block", fontSize:11, fontWeight:700, color:"#9ca3af", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.05em" }}>{children}</label>;
}
function Div() {
  return <div style={{ height:"1.5px", background:"#f3f4f6", margin:"18px 0" }}/>;
}
function PBtn({ children, accent, onClick, loading, type }) {
  return (
    <button type={type || (onClick ? "button" : "submit")} onClick={onClick} disabled={loading}
      style={{ width:"100%", padding:"11px", borderRadius:10, border:"none", background:loading?"#f3f4f6":accent, color:loading?"#9ca3af":"#fff", fontSize:13, fontWeight:700, cursor:loading?"not-allowed":"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8, transition:"opacity .15s", boxShadow:loading?"none":`0 2px 10px ${accent}40` }}
      onMouseEnter={e=>{ if(!loading) e.currentTarget.style.opacity="0.88"; }}
      onMouseLeave={e=>{ e.currentTarget.style.opacity="1"; }}>
      {loading && <svg style={{ animation:"spin 0.8s linear infinite", flexShrink:0 }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>}
      {loading ? "Se salvează..." : children}
    </button>
  );
}
